import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

// Game-specific screen size breakpoints (optimized for card games)
export const gameScreenSizes = {
  xs: 480,  // Small phones
  sm: 640,  // Large phones
  md: 768,  // Small tablets
  lg: 1024, // Large tablets
  xl: 1280, // Small laptops
  '2xl': 1536, // Large laptops/desktops
  '3xl': 1920  // Extra large screens
};

// Game element sizing based on screen dimensions
export const gameElementSizes = {
  // Card sizes for different screen sizes - width and height in pixels
  cards: {
    xs: { width: 60, height: 100 },
    sm: { width: 70, height: 110 },
    md: { width: 80, height: 130 },
    lg: { width: 90, height: 140 },
    xl: { width: 100, height: 160 },
    '2xl': { width: 110, height: 180 },
    '3xl': { width: 120, height: 200 }
  },
  
  // Card spacing for different screen sizes (gap between cards)
  spacing: {
    xs: 8,
    sm: 10,
    md: 14,
    lg: 18,
    xl: 22,
    '2xl': 26,
    '3xl': 30
  },
  
  // Animation speeds (in ms) for different screen sizes
  animationSpeed: {
    xs: { fast: 150, normal: 300, slow: 500 },
    sm: { fast: 150, normal: 300, slow: 500 },
    md: { fast: 200, normal: 350, slow: 600 },
    lg: { fast: 200, normal: 350, slow: 600 },
    xl: { fast: 250, normal: 400, slow: 700 },
    '2xl': { fast: 250, normal: 400, slow: 700 },
    '3xl': { fast: 300, normal: 450, slow: 800 }
  }
};

/**
 * A hook for responsive design in card games
 * Uses react-responsive under the hood for precise screen size detection
 */
export function useGameResponsive() {
  // Define media queries using react-responsive
  const isExtraSmall = useMediaQuery({ maxWidth: gameScreenSizes.xs });
  const isSmall = useMediaQuery({ minWidth: gameScreenSizes.xs + 1, maxWidth: gameScreenSizes.sm });
  const isMedium = useMediaQuery({ minWidth: gameScreenSizes.sm + 1, maxWidth: gameScreenSizes.md });
  const isLarge = useMediaQuery({ minWidth: gameScreenSizes.md + 1, maxWidth: gameScreenSizes.lg });
  const isXLarge = useMediaQuery({ minWidth: gameScreenSizes.lg + 1, maxWidth: gameScreenSizes.xl });
  const is2XLarge = useMediaQuery({ minWidth: gameScreenSizes.xl + 1, maxWidth: gameScreenSizes['2xl'] });
  const is3XLarge = useMediaQuery({ minWidth: gameScreenSizes['2xl'] + 1 });

  // Orientation detection
  const isPortrait = useMediaQuery({ orientation: 'portrait' });
  const isLandscape = useMediaQuery({ orientation: 'landscape' });

  // Track window dimensions
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    aspectRatio: typeof window !== 'undefined' ? window.innerWidth / window.innerHeight : 0
  });

  // Update dimensions on window resize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
        aspectRatio: window.innerWidth / window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine current screen size
  const getCurrentScreenSize = (): keyof typeof gameScreenSizes => {
    if (isExtraSmall) return 'xs';
    if (isSmall) return 'sm';
    if (isMedium) return 'md';
    if (isLarge) return 'lg';
    if (isXLarge) return 'xl';
    if (is2XLarge) return '2xl';
    return '3xl';
  };

  // Get current screen size
  const screenSize = getCurrentScreenSize();

  // Device category helpers
  const isMobileDevice = isExtraSmall || isSmall;
  const isTabletDevice = isMedium || isLarge;
  const isDesktopDevice = isXLarge || is2XLarge || is3XLarge;

  // Get card size for current screen
  const getCardSize = () => {
    const size = gameElementSizes.cards[screenSize];
    return {
      width: size.width,
      height: size.height,
      aspectRatio: size.width / size.height
    };
  };

  // Get spacing for current screen
  const getSpacing = () => {
    return gameElementSizes.spacing[screenSize];
  };

  // Get animation speeds for current screen
  const getAnimationSpeed = () => {
    return gameElementSizes.animationSpeed[screenSize];
  };

  // Calculate optimal card size based on both width and height constraints
  const getOptimalCardSize = (containerWidth: number, containerHeight: number, cardCount: number) => {
    const baseSize = getCardSize();
    const spacing = getSpacing();
    
    // Calculate maximum width for cards based on container and card count
    const totalSpacingWidth = spacing * (cardCount - 1);
    const availableWidth = containerWidth - totalSpacingWidth;
    const maxCardWidth = Math.floor(availableWidth / cardCount);
    
    // Calculate max height based on container height
    const maxCardHeight = Math.floor(containerHeight * 0.8);
    
    // Maintain aspect ratio while fitting within constraints
    const cardAspectRatio = baseSize.aspectRatio;
    
    let finalWidth, finalHeight;
    
    if (maxCardWidth / cardAspectRatio <= maxCardHeight) {
      // Width is the constraining factor
      finalWidth = Math.min(maxCardWidth, baseSize.width);
      finalHeight = Math.floor(finalWidth / cardAspectRatio);
    } else {
      // Height is the constraining factor
      finalHeight = Math.min(maxCardHeight, baseSize.height);
      finalWidth = Math.floor(finalHeight * cardAspectRatio);
    }
    
    return {
      width: finalWidth,
      height: finalHeight,
      spacing
    };
  };

  return {
    screenSize,
    dimensions,
    isPortrait,
    isLandscape,
    isMobileDevice,
    isTabletDevice,
    isDesktopDevice,
    getCardSize,
    getSpacing,
    getAnimationSpeed,
    getOptimalCardSize
  };
}
